import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./Taluka.css";
import { useSelector, useDispatch } from "react-redux";
import { baseUrl } from "../../../utils/httpService";
import Top5FeaturedNews from "./Top5FeaturedNews";
import NewsCard from "./NewsCard";
// import GoogleNewsSidebar from "./GoogleNewsSidebar";
import { AllViewCountActionFetchData } from "../../../redux/actions/AllViewCountAction";
import { fetchTalukaUrls } from "../../../utils/talukaCache";
import { generateResizedImageUrl } from "../../../utils/httpService";

const Taluka = ({ allData, cityData }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const filterScrollRef = useRef(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [imgError, setImgError] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const { assetbaseUrl } = useSelector(
    (state) => state?.SettingReducer?.data || {}
  );
  useEffect(() => {
    fetchTalukaUrls();
  }, [])

  const handleScrollLeft = () => {
    if (filterScrollRef.current) {
      filterScrollRef.current.scrollBy({ left: -220, behavior: "smooth" });
    }
  };

  const handleScrollRight = () => {
    if (filterScrollRef.current) {
      filterScrollRef.current.scrollBy({ left: 220, behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (cityData) {
      setSelectedCity(cityData);
    } else if (allData && allData.length > 0) {
      // Default to first city if none selected
      setSelectedCity(allData[0]);
      navigate(`/${allData[0].url}`, { replace: true });
    }
  }, [cityData, allData, navigate]);

  useEffect(() => {
    setImgError(false);
    setPage(1);
    setSearchResults([]);
    setHasMore(true);

    if (selectedCity) {
      dispatch(AllViewCountActionFetchData(window.location.pathname));
    }

    // Scroll the active city tab into view — use manual scrollLeft
    // instead of scrollIntoView to avoid scrolling parent containers
    setTimeout(() => {
      if (filterScrollRef.current) {
        const container = filterScrollRef.current;
        const activeElement = container.querySelector('.hero-city-chip.active');
        if (activeElement) {
          const containerRect = container.getBoundingClientRect();
          const activeRect = activeElement.getBoundingClientRect();
          const scrollOffset = activeRect.left - containerRect.left - (containerRect.width / 2) + (activeRect.width / 2);
          container.scrollBy({ left: scrollOffset, behavior: 'smooth' });
        }
      }
    }, 100);
  }, [selectedCity]);

  // Fetch search API results for selected city
  useEffect(() => {
    if (!selectedCity) return;
    if (page === 1) {
      setSearchLoading(true);
    } else {
      setLoadingMore(true);
    }
    const keyword = selectedCity.url ? selectedCity.url.replace("-news", "") : (selectedCity.gujarati_name || selectedCity.name || "");
    fetch(`${baseUrl}search?keyword=${encodeURIComponent(keyword)}&limit=12&page=${page}`)
      .then((res) => res.json())
      .then((json) => {
        if (json.status && json.data) {
          const list = Array.isArray(json.data)
            ? json.data
            : (json.data.data || json.data.result || []);
          if (page === 1) {
            setSearchResults(list);
          } else {
            setSearchResults(prev => [...prev, ...list]);
          }
          if (list.length < 12) {
            setHasMore(false);
          }
        } else {
          if (page === 1) setSearchResults([]);
          setHasMore(false);
        }
        setSearchLoading(false);
        setLoadingMore(false);
      })
      .catch((err) => {
        console.error("Error fetching search API:", err);
        if (page === 1) setSearchResults([]);
        setSearchLoading(false);
        setLoadingMore(false);
        setHasMore(false);
      });
  }, [selectedCity, page]);

  if (!selectedCity) {
    return <div className="text-center py-5" style={{ minHeight: "50vh" }}>No taluka data available. Please try again later.</div>;
  }

  const handleCityClick = (url) => {
    navigate(`/${url}`, { state: { preventScrollTop: true } });
  };

  const handleTalukaClick = (url) => {
    navigate(`/${url}`);
  };

  const getCityImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }
    const cleanPath = path.startsWith("/") ? path.slice(1) : path;
    return `${cleanPath}`;
  };

  const currentCityImgSrc = getCityImageUrl(selectedCity?.image_path);

  const apiNews = selectedCity?.news || selectedCity?.articles || selectedCity?.news_list || [];
  const displayItems = (searchResults && searchResults.length > 0)
    ? searchResults
    : apiNews;

  return (
    <div className="taluka-container container">

      <div className="row mt-4">
        {/* Left Column: Main Content */}
        <div className="col-lg-9 col-md-12 pr-lg-4">
          {/* Hero Header with Integrated City Filters */}
          <div className="taluka-hero-banner">
            <div className="hero-bg-shapes">
              <div className="hero-circle hero-circle-1"></div>
              <div className="hero-circle hero-circle-2"></div>
              <div className="hero-circle hero-circle-3"></div>
            </div>
            <div className="hero-top-row">
              <div className="hero-title-group">
                <div className="hero-icon-wrap">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                </div>
                <div>
                  <h1 className="hero-main-title">
                    <span className="title-highlight">{selectedCity.name}</span>{" "}

                  </h1>
                </div>
              </div>
            </div>
            <div className="hero-city-filter-wrapper">
              <button
                type="button"
                className="filter-scroll-arrow left-arrow"
                onClick={handleScrollLeft}
                aria-label="Scroll Left"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>

              <div className="hero-city-filters" ref={filterScrollRef}>
                {allData?.map((city) => (
                  <div
                    key={city.id}
                    className={`hero-city-chip ${selectedCity.id === city.id ? "active" : ""}`}
                    onClick={() => handleCityClick(city.url)}
                  >
                    <span className="chip-icon-box">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                    </span>
                    {city.gujarati_name || city.name}
                  </div>
                ))}
              </div>

              <button
                type="button"
                className="filter-scroll-arrow right-arrow"
                onClick={handleScrollRight}
                aria-label="Scroll Right"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            </div>
          </div>

          {/* Selected City Row Layout (2 Columns) */}
          <div className="selected-city-row mt-4">
            {/* Left Column: Image and Name */}
            <div className="city-left-col">
              <div className="city-image-container">
                {currentCityImgSrc && !imgError ? (
                  <img
                    src={generateResizedImageUrl(550, 413, currentCityImgSrc)}
                    alt={selectedCity.gujarati_name || selectedCity.name}
                    className="city-hero-img"
                    onError={() => setImgError(true)}
                  />
                ) : (
                  <div className="city-hero-fallback-bg"></div>
                )}
                <div className="image-overlay-gradient"></div>
                <div className="city-hero-content">
                  <h2 className="city-hero-name">
                    {selectedCity.gujarati_name || selectedCity.name}
                  </h2>
                </div>
              </div>
            </div>

            {/* Right Column: Description */}
            <div className="city-right-col">
              <h3>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ae0a0b" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "2px" }}>
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
                <span style={{ flex: 1 }}>
                  <span className="title-highlight">{selectedCity.gujarati_name || selectedCity.name}</span> જિલ્લા વિશે માહિતી
                </span>
              </h3>
              <div
                className="city-description"
                dangerouslySetInnerHTML={{ __html: selectedCity.description || "<p>માહિતી ઉપલબ્ધ નથી.</p>" }}
              ></div>
            </div>
          </div>

          {/* Talukas Section */}
          <div className="talukas-section">
            <h3 className="section-title">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ae0a0b" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon>
                <line x1="8" y1="2" x2="8" y2="18"></line>
                <line x1="16" y1="6" x2="16" y2="22"></line>
              </svg>
              <span style={{ flex: 1 }}>
                <span className="title-highlight">{selectedCity.gujarati_name || selectedCity.name}</span> ના તાલુકાઓ
              </span>
            </h3>
            <div className="talukas-grid">
              {selectedCity.talukas && selectedCity.talukas.length > 0 ? (
                selectedCity.talukas.map((taluka) => (
                  <div
                    key={taluka.id}
                    className="taluka-card"
                    onClick={() => handleTalukaClick(taluka.url)}
                  >
                    <div className="taluka-card-content">
                      <div className="taluka-card-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 2a8 8 0 0 0-8 8c0 5.25 8 12 8 12s8-6.75 8-12a8 8 0 0 0-8-8z"></path>
                          <circle cx="12" cy="10" r="2.5"></circle>
                        </svg>
                      </div>
                      <div className="taluka-card-name">
                        {taluka.gujarati_name || taluka.name}
                      </div>
                    </div>
                    <div className="taluka-card-arrow">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                        <polyline points="12 5 19 12 12 19"></polyline>
                      </svg>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "20px", color: "#64748b" }}>
                  કોઈ તાલુકા માહિતી ઉપલબ્ધ નથી
                </div>
              )}
            </div>
          </div>

          {/* City Related News */}
          <div className="city-news-section mt-5">
            <h2 className="section-title">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ae0a0b" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <path d="M19 20H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v1m2 13a2 2 0 0 1-2-2V7m2 13a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2"></path>
                <line x1="7" y1="8" x2="11" y2="8"></line>
                <line x1="7" y1="12" x2="13" y2="12"></line>
                <line x1="7" y1="16" x2="13" y2="16"></line>
              </svg>
              <span style={{ flex: 1 }}>
                આજના <span className="title-highlight">{selectedCity.gujarati_name || selectedCity.name}</span> જિલ્લાના સમાચાર
              </span>
            </h2>

            <Top5FeaturedNews items={displayItems.slice(0, 6)} talukaName={selectedCity.gujarati_name || selectedCity.name} />

            <div className="row">
              {searchLoading && page === 1 ? (
                <div className="col-12 text-center py-4 text-muted">
                  સમાચાર લોડ થઈ રહ્યા છે...
                </div>
              ) : displayItems && displayItems.length === 0 ? (
                <div className="col-12 mt-2 mb-4">
                  <div className="py-5 bg-white text-center" style={{ borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
                    <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "72px", height: "72px", borderRadius: "50%", background: "#f8fafc", marginBottom: "16px" }}>
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 22h14a2 2 0 0 0 2-2V7.5L14.5 2H6a2 2 0 0 0-2 2v4"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="8" y1="13" x2="16" y2="13"></line>
                        <line x1="8" y1="17" x2="16" y2="17"></line>
                      </svg>
                    </div>
                    <h4 style={{ color: "#334155", fontWeight: "600", fontSize: "20px", marginBottom: "8px" }}>કોઈ સમાચાર ઉપલબ્ધ નથી</h4>
                    <p style={{ color: "#64748b", fontSize: "16px", margin: 0 }}>
                      <span className="title-highlight">{selectedCity.gujarati_name || selectedCity.name}</span> તાલુકાના નવા સમાચાર ટૂંક સમયમાં અપડેટ કરવામાં આવશે.
                    </p>
                  </div>
                </div>
              ) : displayItems && displayItems.length > 6 ? (
                displayItems.slice(6).map((item, index) => (
                  <div className="col-lg-4 col-md-6 mb-4" key={item.id || index}>
                    <NewsCard
                      item={item}

                      fallbackCategoryName={selectedCity.gujarati_name || selectedCity.name}
                    />
                  </div>
                ))
              ) : null}

              {hasMore && displayItems && displayItems.length > 0 && (
                <div className="col-12 text-center mt-4 mb-2">
                  <button
                    className="btn btn-dark"
                    style={{ borderRadius: "20px", padding: "8px 24px", fontWeight: "600", fontSize: "14px", backgroundColor: "#1e293b" }}
                    onClick={() => setPage(prev => prev + 1)}
                    disabled={loadingMore}
                  >
                    {loadingMore ? "લોડ થઈ રહ્યું છે..." : "વધુ સમાચાર જુઓ"} <i className="fa fa-angle-down ml-1"></i>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* End Left Column */}
        </div>

        {/* Right Column: Google News Follow Card + Ad Section */}
        <div className="col-lg-3 col-md-12 mb-4 mb-lg-0">
          {/* <GoogleNewsSidebar /> */}
        </div>

        {/* End Row */}
      </div>

    </div>
  );
};

export default Taluka;
